import { useRouteError, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Home, RefreshCw, ArrowLeft } from "lucide-react";

const VITE_ENV = import.meta.env.VITE_ENV;

interface RouterError extends Error {
    status?: number;
    statusText?: string;
    data?: any;
}

const RouterErrorElement = () => {
    const error = useRouteError() as RouterError;
    const navigate = useNavigate();
    const [countdown, setCountdown] = useState(5);

    const isDynamicImportError = (error: any): boolean => {
        if (!error) return false;

        const errorMessage = error?.message?.toLowerCase() || "";
        const errorString = error?.toString?.()?.toLowerCase() || "";

        return (
            // Dynamic import specific errors
            errorMessage.includes(
                "error loading dynamically imported module",
            ) ||
            errorMessage.includes("failed to import") ||
            errorMessage.includes("loading chunk") ||
            errorMessage.includes("loading css chunk") ||
            errorMessage.includes("import() failed") ||
            errorMessage.includes("dynamic import") ||
            errorMessage.includes("loading module") ||
            // Network/fetch errors for modules
            (errorMessage.includes("fetch") &&
                errorMessage.includes("module")) ||
            errorMessage.includes("networkerror") ||
            // Module not found
            error?.code === "MODULE_NOT_FOUND" ||
            error?.name?.toLowerCase() === "modulenotfounderror" ||
            // Script/resource loading errors
            errorMessage.includes("loading script") ||
            errorMessage.includes("script error") ||
            // Check error string representation as well
            errorString.includes("loading dynamically imported module") ||
            errorString.includes("failed to import") ||
            errorString.includes("loading chunk")
        );
    };

    const handleDynamicImportError = () => {
        console.log(
            "🚨 RouterErrorElement caught dynamic import error:",
            error,
        );

        const hasReloaded = sessionStorage.getItem("hasReloadedOnRouterError");
        const reloadCount = parseInt(
            sessionStorage.getItem("routerErrorReloadCount") || "0",
        );

        // Prevent infinite reload loops - max 2 attempts
        if (!hasReloaded && reloadCount < 2) {
            sessionStorage.setItem("hasReloadedOnRouterError", "true");
            sessionStorage.setItem(
                "routerErrorReloadCount",
                (reloadCount + 1).toString(),
            );

            console.log(
                "🔄 Force reloading due to router dynamic import error",
            );

            // Clear cache and force reload
            if ("caches" in window) {
                caches
                    .keys()
                    .then((names: string[]) => {
                        const deletePromises = names.map((name: string) =>
                            caches.delete(name),
                        );
                        return Promise.all(deletePromises);
                    })
                    .then(() => {
                        console.log(
                            "🧹 Browser caches cleared by RouterErrorElement",
                        );
                    })
                    .catch((err) => {
                        console.warn("⚠️ Error clearing caches:", err);
                    })
                    .finally(() => {
                        forceReload();
                    });
            } else {
                forceReload();
            }
        } else {
            console.warn("⚠️ Max reload attempts reached for router error");
        }
    };

    const forceReload = () => {
        // Force reload with cache bypass
        const url = new URL(window.location.href);
        url.searchParams.set("_routerReload", Date.now().toString());
        window.location.href = url.toString();
    };

    useEffect(() => {
        console.log("🔍 RouterErrorElement checking error:", {
            error,
            message: error?.message,
            name: error?.name,
            isDynamicImport: isDynamicImportError(error),
        });

        if (isDynamicImportError(error)) {
            console.log(
                "✅ RouterErrorElement detected dynamic import error, initiating reload...",
            );

            // Clear any existing flags to ensure reload works
            sessionStorage.removeItem("hasReloadedOnRouterError");

            // Start countdown
            const countdownInterval = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(countdownInterval);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            // Reload after 5 seconds
            const reloadTimeout = setTimeout(() => {
                console.log("⏰ 5 seconds elapsed, forcing reload now...");
                handleDynamicImportError();
            }, 5000);

            return () => {
                clearInterval(countdownInterval);
                clearTimeout(reloadTimeout);
            };
        } else {
            console.log(
                "❌ RouterErrorElement determined this is NOT a dynamic import error",
            );
        }

        // Clear success flags after component mounts successfully elsewhere
        const clearFlags = () => {
            sessionStorage.removeItem("hasReloadedOnRouterError");
            sessionStorage.removeItem("routerErrorReloadCount");
        };

        // Clear flags after delay if we don't reload
        const timeoutId = setTimeout(clearFlags, 30000);
        return () => clearTimeout(timeoutId);
    }, [error]);

    const retryNavigation = () => {
        // Try to navigate back or to home
        try {
            navigate(-1);
        } catch {
            navigate("/");
        }
    };

    const isDynamicImport = isDynamicImportError(error);
    const statusCode = error?.status || (isDynamicImport ? 503 : 500);

    return (
        <div
            role="alert"
            aria-live="assertive"
            className="flex min-h-screen w-full flex-col items-center justify-center bg-gray-50 p-4"
        >
            <div className="max-w-md space-y-6 text-center">
                {/* Error Status Code - Hide for dynamic import errors */}
                {!isDynamicImport && (
                    <h1 className="text-8xl font-bold text-gray-900">
                        {statusCode}
                    </h1>
                )}

                {/* Error Message */}
                <div className="space-y-2">
                    <h2 className="text-2xl font-semibold text-gray-800">
                        {isDynamicImport ? "Software Updated" : "Page Error"}
                    </h2>
                    <p className="text-gray-600">
                        {isDynamicImport
                            ? "We've updated the application. We'll reload the page for you to apply the latest changes."
                            : VITE_ENV === "DEV" &&
                              (error?.message ||
                                  error?.statusText ||
                                  "An unexpected error occurred while loading this page.")}
                    </p>
                </div>

                {isDynamicImport && (
                    <div className="rounded-md border border-blue-200 bg-blue-50 p-6 text-blue-900">
                        <div className="mb-2 text-5xl font-bold">
                            {countdown}
                        </div>
                        <strong>
                            Reloading in {countdown}{" "}
                            {countdown === 1 ? "second" : "seconds"}...
                        </strong>
                        <br />
                        <small className="text-blue-700">
                            Please wait while we refresh the page.
                        </small>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap justify-center gap-2">
                    <Button
                        onClick={retryNavigation}
                        variant="outline"
                        className="inline-flex items-center gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Go Back
                    </Button>

                    <Button
                        onClick={() => navigate("/")}
                        className="inline-flex items-center gap-2"
                    >
                        <Home className="h-4 w-4" />
                        Back to Home
                    </Button>

                    <Button
                        onClick={forceReload}
                        variant="secondary"
                        className="inline-flex items-center gap-2"
                    >
                        <RefreshCw className="h-4 w-4" />
                        Force Refresh
                    </Button>
                </div>

                {/* Show error details in development */}
                {VITE_ENV === "DEV" && (
                    <details className="mt-8 w-full max-w-full text-left">
                        <summary className="mb-4 cursor-pointer font-bold">
                            Error Details (Development Only)
                        </summary>
                        <pre className="overflow-auto whitespace-pre-wrap break-words rounded bg-gray-100 p-4 text-sm">
                            <strong>Error:</strong> {error?.name || "Unknown"}
                            {"\n"}
                            <strong>Message:</strong>{" "}
                            {error?.message || "No message"}
                            {"\n"}
                            <strong>Status:</strong> {error?.status || "N/A"}
                            {"\n"}
                            <strong>Status Text:</strong>{" "}
                            {error?.statusText || "N/A"}
                            {"\n"}
                            {error?.stack && (
                                <>
                                    <strong>Stack:</strong>
                                    {"\n"}
                                    {error.stack}
                                </>
                            )}
                        </pre>
                    </details>
                )}
            </div>
        </div>
    );
};

export default RouterErrorElement;
